/**
 * DebtProvisionChart — Recharts chart for revolving debt provision tracking.
 *
 * Shows:
 *   - Bar chart: monthly provision amounts per month.
 *   - Line: cumulative provision accumulated over time.
 *   - Reference line at drawn_total_cents (target to reach before in fine date).
 *   - Reference line at the in fine date.
 *
 * Props:
 *   schedule          : [{ date:'YYYY-MM-01', amount_cents, breakdown:[] }]
 *   drawnTotalCents   : number — total amount drawn (target for provision)
 *   plannedRepayment  : 'YYYY-MM-DD' | null — in fine date
 */

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';

function formatXLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[200px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        p.value != null && (
          <p key={p.dataKey} style={{ color: p.color ?? p.stroke }} className="flex justify-between gap-3">
            <span>{p.name}</span>
            <span className="tabular-nums font-medium">{formatCurrency(p.value * 100)}</span>
          </p>
        )
      ))}
    </div>
  );
}

function buildChartData(schedule) {
  let cumulative = 0;
  return schedule.map((row) => {
    cumulative += (row.amount_cents ?? 0);
    return {
      label: formatXLabel(row.date),
      date: row.date,
      monthly: (row.amount_cents ?? 0) / 100,
      cumulative: cumulative / 100,
    };
  });
}

export default function DebtProvisionChart({
  schedule = [],
  drawnTotalCents = 0,
  plannedRepayment = null,
}) {
  if (!schedule.length) {
    return (
      <div className="flex items-center justify-center h-[220px] text-sm text-slate-400">
        Pas de données de provision
      </div>
    );
  }

  const chartData = buildChartData(schedule);
  const drawnTotalEuros = drawnTotalCents / 100;

  // Find the label closest to the in fine date for the ReferenceLine
  const inFineLabel = (() => {
    if (!plannedRepayment) return null;
    const target = plannedRepayment.slice(0, 7) + '-01';
    const entry = chartData.find((d) => d.date >= target);
    return entry?.label ?? chartData[chartData.length - 1]?.label ?? null;
  })();

  return (
    <div className="w-full" style={{ minHeight: 220 }}>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            tickFormatter={(v) => formatCurrencyShort(v * 100)}
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={64}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />

          {/* Target line = montant total tiré (objectif de couverture) */}
          {drawnTotalEuros > 0 && (
            <ReferenceLine
              y={drawnTotalEuros}
              stroke="#a855f7"
              strokeDasharray="5 4"
              strokeWidth={1.5}
              label={{ value: 'Objectif', position: 'insideTopRight', fontSize: 10, fill: '#a855f7' }}
            />
          )}

          {/* In fine date marker */}
          {inFineLabel && (
            <ReferenceLine
              x={inFineLabel}
              stroke="#ef4444"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              label={{ value: 'In fine', position: 'insideTopRight', fontSize: 10, fill: '#ef4444' }}
            />
          )}

          {/* Monthly provision bars */}
          <Bar
            dataKey="monthly"
            name="Provision mensuelle"
            fill="#c4b5fd"
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          />

          {/* Cumulative provision line */}
          <Line
            type="monotone"
            dataKey="cumulative"
            name="Cumul provision"
            stroke="#7c3aed"
            strokeWidth={2}
            dot={false}
            connectNulls
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
